import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, Mail, Phone, Calendar as CalendarIcon, Users, Home, ArrowLeft, Edit, Trash2, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ImageUploader } from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPage() {
  const navigate = useNavigate();
  const [bookingRequests, setBookingRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [editingProperty, setEditingProperty] = useState(null);
  const [showAddProperty, setShowAddProperty] = useState(false);

  const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

  useEffect(() => {
    if (authenticated) {
      fetchData();
    }
  }, [authenticated]);

  const fetchData = async () => {
    try {
      const [requestsRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/booking-requests`),
        axios.get(`${API}/properties`)
      ]);
      setBookingRequests(requestsRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/calendar/sync`);
      toast.success('Calendar synchronization started');
      setTimeout(() => {
        fetchData();
        setSyncing(false);
      }, 3000);
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Synchronization error');
      setSyncing(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/booking-requests/${requestId}?status=${newStatus}`);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Update error');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      toast.success('Successfully logged in');
    } else {
      toast.error('Incorrect password');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) {
      return;
    }
    try {
      await axios.delete(`${API}/properties/${propertyId}`);
      toast.success('Property deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Delete error');
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl">Administration</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button
                data-testid="admin-login-btn"
                type="submit"
                className="w-full bg-accent hover:bg-accent/90"
              >
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4 md:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-home-btn"
              variant="ghost"
              onClick={() => navigate('/')}
            >
              <ArrowLeft size={20} className="mr-2" />
              Back
            </Button>
            <h1 className="text-3xl md:text-4xl font-bold">Administration</h1>
          </div>
          <Button
            data-testid="sync-calendars-btn"
            onClick={handleSync}
            disabled={syncing}
            variant="outline"
          >
            <RefreshCw size={20} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Calendars'}
          </Button>
        </div>

        <Tabs defaultValue="requests" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="requests">Booking Requests</TabsTrigger>
            <TabsTrigger value="properties">Manage Properties</TabsTrigger>
            <TabsTrigger value="add">Add Property</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-6">
            <BookingRequestsTab
              requests={bookingRequests}
              loading={loading}
              onStatusUpdate={handleStatusUpdate}
            />
          </TabsContent>

          <TabsContent value="properties" className="mt-6">
            <PropertiesManagementTab
              properties={properties}
              onEdit={setEditingProperty}
              onDelete={handleDeleteProperty}
              onRefresh={fetchData}
            />
          </TabsContent>

          <TabsContent value="add" className="mt-6">
            <AddPropertyTab onSuccess={fetchData} />
          </TabsContent>
        </Tabs>

        {editingProperty && (
          <EditPropertyDialog
            property={editingProperty}
            onClose={() => setEditingProperty(null)}
            onSuccess={fetchData}
          />
        )}
      </div>
    </div>
  );
}

function BookingRequestsTab({ requests, loading, onStatusUpdate }) {
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          No booking requests yet
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((request) => (
        <Card key={request.id} className="border-2">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-lg">{request.property_name}</h3>
                  <Badge
                    variant={request.status === 'pending' ? 'secondary' : request.status === 'confirmed' ? 'default' : 'destructive'}
                  >
                    {request.status === 'pending' && 'Pending'}
                    {request.status === 'confirmed' && 'Confirmed'}
                    {request.status === 'cancelled' && 'Cancelled'}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users size={16} />
                    <span>{request.guest_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail size={16} />
                    <span>{request.guest_email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone size={16} />
                    <span>{request.guest_phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CalendarIcon size={16} />
                    <span>{request.check_in} → {request.check_out}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users size={16} />
                  <span>{request.guests} guest(s)</span>
                </div>

                {request.message && (
                  <div className="text-sm bg-muted/50 p-3 rounded-lg">
                    <p className="font-medium mb-1">Message:</p>
                    <p className="text-muted-foreground">{request.message}</p>
                  </div>
                )}
              </div>

              <div className="flex md:flex-col gap-2">
                {request.status === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => onStatusUpdate(request.id, 'confirmed')}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => onStatusUpdate(request.id, 'cancelled')}
                    >
                      Decline
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PropertiesManagementTab({ properties, onEdit, onDelete, onRefresh }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {properties.map((property) => (
        <Card key={property.id} className="overflow-hidden">
          <div className="aspect-video relative">
            <img
              src={property.images[0]}
              alt={property.name}
              className="w-full h-full object-cover"
            />
          </div>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold mb-1">{property.name}</h3>
                <Badge>{property.type}</Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onEdit(property)}
                >
                  <Edit size={16} />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onDelete(property.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
              {property.description}
            </p>

            <div className="grid grid-cols-2 gap-2 text-sm mb-4">
              <div>Price: €{property.price_min} - €{property.price_max}/night</div>
              <div>Guests: {property.max_guests}</div>
              <div>Bedrooms: {property.bedrooms}</div>
              <div>Bathrooms: {property.bathrooms}</div>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.location.href = `/#/admin/calendar/${property.id}`}
                className="flex-1"
              >
                Manage Calendar
              </Button>
            </div>

            {property.ical_url && (
              <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                iCal: {property.ical_url.substring(0, 50)}...
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AddPropertyTab({ onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'apartment',
    description: '',
    price_min: 100,
    price_max: 150,
    max_guests: 2,
    bedrooms: 1,
    bathrooms: 1,
    images: [],
    amenities: [''],
    ical_url: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const cleanData = {
        ...formData,
        images: formData.images.filter(img => img.trim()),
        amenities: formData.amenities.filter(a => a.trim()),
        ical_url: formData.ical_url.trim() || null
      };

      await axios.post(`${API}/properties`, cleanData);
      toast.success('Property added successfully!');
      onSuccess();
      
      // Reset form
      setFormData({
        name: '',
        type: 'apartment',
        description: '',
        price_per_night: 100,
        max_guests: 2,
        bedrooms: 1,
        bathrooms: 1,
        images: [''],
        amenities: [''],
        ical_url: ''
      });
    } catch (error) {
      console.error('Error adding property:', error);
      toast.error('Error adding property');
    } finally {
      setSubmitting(false);
    }
  };

  const addImageField = () => {
    setFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const removeImageField = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const updateImage = (index, value) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const addAmenityField = () => {
    setFormData(prev => ({ ...prev, amenities: [...prev.amenities, ''] }));
  };

  const removeAmenityField = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const updateAmenity = (index, value) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.map((a, i) => i === index ? value : a)
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add New Property</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Property Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                required
              >
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="house">House</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2 col-span-2 md:col-span-1">
              <Label htmlFor="price_min">Min Price (€) *</Label>
              <Input
                id="price_min"
                type="number"
                min="0"
                value={formData.price_min}
                onChange={(e) => setFormData(prev => ({ ...prev, price_min: parseFloat(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2 col-span-2 md:col-span-1">
              <Label htmlFor="price_max">Max Price (€) *</Label>
              <Input
                id="price_max"
                type="number"
                min="0"
                value={formData.price_max}
                onChange={(e) => setFormData(prev => ({ ...prev, price_max: parseFloat(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="guests">Max Guests *</Label>
              <Input
                id="guests"
                type="number"
                min="1"
                value={formData.max_guests}
                onChange={(e) => setFormData(prev => ({ ...prev, max_guests: parseInt(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bedrooms">Bedrooms *</Label>
              <Input
                id="bedrooms"
                type="number"
                min="1"
                value={formData.bedrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: parseInt(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bathrooms">Bathrooms *</Label>
              <Input
                id="bathrooms"
                type="number"
                min="1"
                value={formData.bathrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: parseInt(e.target.value) }))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Property Images *</Label>
            <ImageUploader
              images={Array.isArray(formData.images) ? formData.images : []}
              onChange={(newImages) => {
                console.log('Images updated:', newImages);
                setFormData(prev => ({ ...prev, images: newImages }));
              }}
            />
            <p className="text-xs text-muted-foreground">Current images: {formData.images.length}</p>
          </div>

          <div className="space-y-2">
            <Label>Amenities</Label>
            {formData.amenities.map((amenity, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={amenity}
                  onChange={(e) => updateAmenity(index, e.target.value)}
                  placeholder="Wi-Fi, Pool, etc."
                />
                {formData.amenities.length > 1 && (
                  <Button type="button" variant="destructive" size="sm" onClick={() => removeAmenityField(index)}>
                    <X size={16} />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addAmenityField}>
              <Plus size={16} className="mr-2" /> Add Amenity
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ical">iCal URL (Airbnb/VRBO)</Label>
            <Input
              id="ical"
              value={formData.ical_url}
              onChange={(e) => setFormData(prev => ({ ...prev, ical_url: e.target.value }))}
              placeholder="https://www.airbnb.com/calendar/ical/..."
            />
            <p className="text-xs text-muted-foreground">Optional: Add your Airbnb or VRBO calendar URL for automatic synchronization</p>
          </div>

          <Button
            type="submit"
            className="w-full bg-accent hover:bg-accent/90"
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add Property'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function EditPropertyDialog({ property, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: property.name,
    type: property.type,
    description: property.description,
    price_min: property.price_min || 100,
    price_max: property.price_max || 150,
    max_guests: property.max_guests,
    bedrooms: property.bedrooms,
    bathrooms: property.bathrooms,
    images: property.images,
    amenities: property.amenities,
    ical_url: property.ical_url || ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const cleanData = {
        ...formData,
        images: formData.images.filter(img => img.trim()),
        amenities: formData.amenities.filter(a => a.trim()),
        ical_url: formData.ical_url.trim() || null
      };

      await axios.put(`${API}/properties/${property.id}`, cleanData);
      toast.success('Property updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating property:', error);
      toast.error('Error updating property');
    } finally {
      setSubmitting(false);
    }
  };

  const addImageField = () => {
    setFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const removeImageField = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const updateImage = (index, value) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const addAmenityField = () => {
    setFormData(prev => ({ ...prev, amenities: [...prev.amenities, ''] }));
  };

  const removeAmenityField = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const updateAmenity = (index, value) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.map((a, i) => i === index ? value : a)
    }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Property</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Property Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-type">Type *</Label>
              <select
                id="edit-type"
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                required
              >
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="house">House</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description *</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2 col-span-2 md:col-span-1">
              <Label htmlFor="edit-price_min">Min Price (€) *</Label>
              <Input
                id="edit-price_min"
                type="number"
                min="0"
                value={formData.price_min}
                onChange={(e) => setFormData(prev => ({ ...prev, price_min: parseFloat(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2 col-span-2 md:col-span-1">
              <Label htmlFor="edit-price_max">Max Price (€) *</Label>
              <Input
                id="edit-price_max"
                type="number"
                min="0"
                value={formData.price_max}
                onChange={(e) => setFormData(prev => ({ ...prev, price_max: parseFloat(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-guests">Max Guests *</Label>
              <Input
                id="edit-guests"
                type="number"
                min="1"
                value={formData.max_guests}
                onChange={(e) => setFormData(prev => ({ ...prev, max_guests: parseInt(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-bedrooms">Bedrooms *</Label>
              <Input
                id="edit-bedrooms"
                type="number"
                min="1"
                value={formData.bedrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: parseInt(e.target.value) }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-bathrooms">Bathrooms *</Label>
              <Input
                id="edit-bathrooms"
                type="number"
                min="1"
                value={formData.bathrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: parseInt(e.target.value) }))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Property Images *</Label>
            <ImageUploader
              images={Array.isArray(formData.images) ? formData.images : []}
              onChange={(newImages) => {
                console.log('Images updated:', newImages);
                setFormData(prev => ({ ...prev, images: newImages }));
              }}
            />
            <p className="text-xs text-muted-foreground">Current images: {formData.images.length}</p>
          </div>

          <div className="space-y-2">
            <Label>Amenities</Label>
            {formData.amenities.map((amenity, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={amenity}
                  onChange={(e) => updateAmenity(index, e.target.value)}
                  placeholder="Wi-Fi, Pool, etc."
                />
                {formData.amenities.length > 1 && (
                  <Button type="button" variant="destructive" size="sm" onClick={() => removeAmenityField(index)}>
                    <X size={16} />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addAmenityField}>
              <Plus size={16} className="mr-2" /> Add Amenity
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-ical">iCal URL (Airbnb/VRBO)</Label>
            <Input
              id="edit-ical"
              value={formData.ical_url}
              onChange={(e) => setFormData(prev => ({ ...prev, ical_url: e.target.value }))}
              placeholder="https://www.airbnb.com/calendar/ical/..."
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="submit"
              className="flex-1 bg-accent hover:bg-accent/90"
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Update Property'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AdminPage;
